import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useAuth } from '../../../contexts/AuthContext';
import { LeaveService } from '../../../services/leave';
import { LeaveBalanceService } from '../../../services/leaveBalanceService';
import { LeaveApprovalService } from '../../../services/leaveApprovalService';
import { LeavePolicyService } from '../../../services/leavePolicyService';
import { LeaveRequest, LeaveType, LeaveStatus } from '../../../types/leave';
import { LeaveBalanceSnapshot } from '../../../types/leavePolicy';
import { HOLIDAYS_2026 } from '../../../data/holidays';
import { LEAVE_YEARS } from './leaveShared';
import { LEAVE_HISTORY_ANCHOR, pendingApprovalLabel, leaveStageLabel } from './leaveShared';
import { expirySummary, expiryTooltip } from '../leaveExpiry';
import { sameIdentity } from '../../../services/identity';
import { leaveDaysFor, datesBetween, isWorkingDay, formatLeaveDays } from '../../../services/leaveDuration';
import { availableDays, balanceProblem } from '../../../services/leaveAvailability';
import { canSeeLeaveOnCalendar, canSeeLeaveReason, canDeleteLeave } from '../../../services/leaveVisibility';
import { loadDirectReports, getUserByEmail } from '../../../services/userLoader';
import { UserRole } from '../../../types/user';
import { notifyLeaveAmended } from '../../../services/inAppNotification';
import { announceLeaveAmended } from '../../../services/leaveNotifications';
import '../LeavePage.css';
import './leavePages.css';
import '../../common/emsPage.css';

const leaveTypeOptions = Object.values(LeaveType);
const holidaysByDate = new Map(HOLIDAYS_2026.map((h) => [h.date, h.name]));

const LEAVE_TYPE_ABBREVIATIONS: Record<string, string> = {
  [LeaveType.CASUAL]: 'Casual',
  [LeaveType.SICK]: 'Sick',
  [LeaveType.EARNED]: 'Earned',
  [LeaveType.COMP_OFF]: 'Comp-Off',
  [LeaveType.MATERNITY]: 'Maternity',
  [LeaveType.PATERNITY]: 'Paternity',
  // Retired as applyable types, but historic requests still carry them — keep the labels so
  // old records render as themselves instead of falling back to the raw stored string.
  'Work From Home': 'WFH',
  'Unpaid Leave': 'Unpaid',
};

const leaveTypeAbbreviation = (type: LeaveType): string => LEAVE_TYPE_ABBREVIATIONS[type] || type;

/**
 * The name a calendar cell shows, and the name the day popup shows.
 *
 * ── Why this is not `employeeName.split(' ')[0]` any more ────────────────────────
 *
 * That was the whole of it, and it has two failure modes that end the same way — a row in the
 * calendar carrying a leave type and NO NAME, while the popup behind it shows the person's full
 * name and looks like a different screen reading different data:
 *
 *   " Bhuvana Chandrika"   a stored name with a leading space splits to an EMPTY first token,
 *                          so the cell renders an empty span. HTML swallows the leading space
 *                          in the popup, which is why only one of the two looked broken.
 *   "b.chandrika@…"        a row that carries only an address makes the entire address the
 *                          "first name": one unbreakable 34-character token in a 130px cell.
 *
 * So the whitespace is collapsed first, and an address is reduced to the closest thing to a
 * name it carries. Nothing here invents a name: a row with neither is still Unknown.
 *
 * ── And the cell carries the WHOLE name, not the first word ───────────────────────
 *
 * A first name was chosen to make it fit; the cost was that two colleagues who share one are
 * indistinguishable on the calendar, and it still did not guarantee a fit — one long word
 * overflows just the same. The name is now printed in full and TRUNCATED BY CSS, so a cell shows
 * as much as it has room for and ends in an ellipsis when it runs out. That is the rule the
 * calendar needed: something readable is always there, and the missing part is a visible …
 * rather than a name that quietly disappeared.
 */
const normaliseName = (value?: string): string => String(value || '').replace(/\s+/g, ' ').trim();

const fullNameOf = (name?: string, email?: string): string =>
  normaliseName(name) || normaliseName(email) || 'Unknown';

const shortNameOf = (name?: string, email?: string): string => {
  const full = normaliseName(name);
  /* A real name goes through whole. CSS decides how much of it fits — see the note above. */
  if (full && !full.includes('@')) return full;
  const local = (normaliseName(email) || full).split('@')[0];
  if (!local) return 'Unknown';
  /* first.last@ → First. Capitalised because it is being read as a name, not as an address. */
  const part = local.split(/[._-]+/)[0] || local;
  return part.charAt(0).toUpperCase() + part.slice(1);
};

type Session = 'FULL' | 'HALF_AM' | 'HALF_PM';

/** One person's leave on one calendar day, as the shared calendar needs it. */
interface DayEntry {
  /** What the calendar cell prints — the full name, ellipsised by CSS when it will not fit. */
  cellName: string;
  fullName: string;
  email: string;
  abbreviation: string;
  typeLabel: string;
  status: LeaveStatus;
  session?: string;
}

/**
 * Leave type to the balance code its entitlement is tracked under.
 *
 * PATERNITY was missing, so a Paternity Leave request resolved to no code at all and the
 * balance panel silently found nothing to check it against. Tested before MATERNITY only for
 * readability — the two words do not overlap as substrings, so order is not load-bearing.
 */
const typeToCode = (type: LeaveType | string): string => {
  const t = String(type).toUpperCase();
  if (t.includes('EARNED')) return 'EL';
  if (t.includes('CASUAL')) return 'CL';
  if (t.includes('SICK')) return 'SL';
  if (t.includes('PATERNITY')) return 'PL';
  if (t.includes('MATERNITY')) return 'ML';
  if (t.includes('COMP')) return 'CO';
  return '';
};

const toISODate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const parseISODate = (value: string): Date => {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
};

const eachDate = (start: string, end: string): string[] => {
  const dates: string[] = [];
  const current = parseISODate(start);
  const last = parseISODate(end);
  while (current <= last) {
    dates.push(toISODate(current));
    current.setDate(current.getDate() + 1);
  }
  return dates;
};

const getMonthDays = (monthDate: Date): Array<{ date: string; inMonth: boolean; weekend: boolean }> => {
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const first = new Date(year, month, 1);
  const start = new Date(first);
  start.setDate(first.getDate() - first.getDay());
  return Array.from({ length: 42 }, (_, index) => {
    const date = new Date(start);
    date.setDate(start.getDate() + index);
    const dow = date.getDay();
    return { date: toISODate(date), inMonth: date.getMonth() === month, weekend: dow === 0 || dow === 6 };
  });
};

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const ApplyLeavePage: React.FC = () => {
  const { user } = useAuth();
  const today = toISODate(new Date());

  const [monthDate, setMonthDate] = useState(() => new Date());
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();

  const [snapshots, setSnapshots] = useState<LeaveBalanceSnapshot[]>([]);
  const [leaves, setLeaves] = useState<LeaveRequest[]>([]);
  /** Emails of this user's direct reports — needed to decide whose pending leave they may see. */
  const [reportEmails, setReportEmails] = useState<string[]>([]);
  /**
   * The manager who owes this employee a decision, for the history table's pending rows.
   *
   * Resolved from User Management rather than read off the leave record: a Leave row carries no
   * managerEmail — the GraphQL type has no such field — and LeaveApprovalsPage already resolves
   * the applicant's manager the same way for its notifications. One source, so the employee and
   * the approver cannot be told different names.
   */
  const [approver, setApprover] = useState<{ name?: string; email?: string }>({});
  /** Date whose "who is away" popup is open, or null. */
  const [dayDetail, setDayDetail] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string>(today);

  // Form State inside Apply Leave Modal
  const [submitting, setSubmitting] = useState(false);
  /** The request being amended, or null when applying fresh. */
  const [editingLeave, setEditingLeave] = useState<LeaveRequest | null>(null);
  const [overlapWarning, setOverlapWarning] = useState<string | null>(null);
  const [attachmentName, setAttachmentName] = useState<string>('');
  const [form, setForm] = useState({
    type: LeaveType.CASUAL,
    startDate: today,
    endDate: today,
    session: 'FULL' as Session,
    reason: '',
  });

  const loadBalances = useCallback(async () => {
    if (user?.email) {
      setSnapshots(await LeaveBalanceService.getBalances(user.email, year));
    }
  }, [user?.email, year]);

  const loadLeaves = useCallback(async () => {
    setLeaves(await LeaveService.listLeaves());
  }, []);

  // Only managers have reports; for everyone else this stays empty and costs nothing.
  useEffect(() => {
    if (!user?.email || user.role !== UserRole.MANAGER) { setReportEmails([]); return; }
    let active = true;
    loadDirectReports(user.email)
      .then(reports => { if (active) setReportEmails(reports.map(r => r.email)); })
      .catch(() => { if (active) setReportEmails([]); });
    return () => { active = false; };
  }, [user?.email, user?.role]);

  /*
   * Resolve the approver's display name once per session, not once per row.
   *
   * `user.managerEmail` gives the address; the directory gives the name. Falling back to the
   * address when the directory has no name for them is deliberate — an email an employee can
   * chase beats a blank, and pendingApprovalLabel handles the no-manager case separately.
   */
  useEffect(() => {
    const managerEmail = user?.managerEmail;
    if (!managerEmail) { setApprover({}); return; }
    let active = true;
    getUserByEmail(managerEmail)
      .then(manager => {
        if (active) setApprover({ name: manager?.fullName || manager?.name, email: managerEmail });
      })
      .catch(() => { if (active) setApprover({ email: managerEmail }); });
    return () => { active = false; };
  }, [user?.managerEmail]);

  const viewer = useMemo(
    () => ({ email: user?.email, role: user?.role, reportEmails }),
    [user?.email, user?.role, reportEmails]
  );

  /**
   * HR's lock period — the grace days an employee keeps to change a request after it starts.
   *
   * It governs editing and deletion only. Applying is deliberately unrestricted: an employee
   * may request past, present or future dates.
   */
  const [lockPeriodDays, setLockPeriodDays] = useState(() => LeavePolicyService.getLockPeriodDays());
  useEffect(() => {
    const refresh = () => setLockPeriodDays(LeavePolicyService.getLockPeriodDays());
    refresh();
    window.addEventListener('leave-policy-updated', refresh);
    return () => window.removeEventListener('leave-policy-updated', refresh);
  }, []);

  useEffect(() => { loadBalances(); }, [loadBalances]);

  useEffect(() => {
    loadLeaves();
    window.addEventListener('leave-requests-updated', loadLeaves);
    window.addEventListener('storage', loadLeaves);
    return () => {
      window.removeEventListener('leave-requests-updated', loadLeaves);
      window.removeEventListener('storage', loadLeaves);
    };
  }, [loadLeaves]);

  // Balances were only ever loaded once on mount/year change — an HR grant/adjustment,
  // approval, or rejection happening while this page is open never refreshed the balance
  // cards shown here. Same sync bug as the Employee Profile drawer, different page.
  useEffect(() => {
    window.addEventListener('leave-balance-updated', loadBalances);
    window.addEventListener('leave-requests-updated', loadBalances);
    window.addEventListener('storage', loadBalances);
    return () => {
      window.removeEventListener('leave-balance-updated', loadBalances);
      window.removeEventListener('leave-requests-updated', loadBalances);
      window.removeEventListener('storage', loadBalances);
    };
  }, [loadBalances]);

  const isHalfDay = form.session !== 'FULL';

  const selectedBalance = useMemo(() => {
    const code = typeToCode(form.type);
    if (!code) return null;
    return snapshots.find((s) => s.leaveTypeCode.toUpperCase() === code) || null;
  }, [snapshots, form.type]);

  /**
   * What this request will actually cost, from the same helper the balance uses.
   *
   * Previously this counted calendar days while the balance counted them differently, so
   * the form could promise one figure and the balance deduct another.
   */
  const duration = useMemo(
    () => leaveDaysFor({
      startDate: form.startDate,
      endDate: isHalfDay ? form.startDate : form.endDate,
      session: form.session,
    }),
    [form.startDate, form.endDate, form.session, isHalfDay]
  );

  /** Non-working days inside the chosen range, so the employee knows why the cost is lower. */
  /*
   * What is left to spend, per type, against the same snapshot the balance card shows.
   *
   * A type with nothing left is offered but disabled in the dropdown, and a request for more
   * days than remain cannot be submitted — see leaveAvailability. Types with no tracked
   * balance (null) are never restricted.
   */
  const editingCode = editingLeave ? typeToCode(editingLeave.type) : '';
  const availableFor = useCallback(
    (type: LeaveType | string): number | null => {
      const code = typeToCode(type);
      if (!code) return null;
      const snapshot = snapshots.find((s) => s.leaveTypeCode.toUpperCase() === code);
      return availableDays(snapshot, editingLeave, editingCode);
    },
    [snapshots, editingLeave, editingCode]
  );
  const balanceError = balanceProblem(
    String(form.type),
    availableFor(form.type),
    duration,
    Boolean(selectedBalance?.dojMissing)
  );

  /*
   * A new request never opens on a type with nothing left. Casual Leave is the default, and an
   * employee who has used all of it would otherwise land on a disabled option and an error
   * before touching anything. Editing is left alone: the request's own type is what is being
   * edited, and its own days are already counted back in.
   */
  useEffect(() => {
    if (!isModalOpen || editingLeave || snapshots.length === 0) return;
    if (availableFor(form.type) !== 0) return;
    const firstUsable = leaveTypeOptions.find((type) => availableFor(type) !== 0);
    if (firstUsable && firstUsable !== form.type) {
      setForm((prev) => ({ ...prev, type: firstUsable }));
    }
  }, [isModalOpen, editingLeave, snapshots, availableFor, form.type]);

  const excludedDays = useMemo(() => {
    if (isHalfDay) return [];
    return datesBetween(form.startDate, form.endDate).filter(d => !isWorkingDay(d));
  }, [form.startDate, form.endDate, isHalfDay]);

  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      if (!user?.email || duration <= 0) { setOverlapWarning(null); return; }
      // Exclude the request being amended, or it would report overlapping with itself.
      const result = await LeaveApprovalService.checkLeaveOverlap(
        user.email, form.startDate, form.endDate, editingLeave?.id
      );
      if (cancelled) return;
      if (result.hasOverlap && result.overlappingRequest) {
        const o = result.overlappingRequest;
        const range = o.startDate === o.endDate ? o.startDate : `${o.startDate} to ${o.endDate}`;
        setOverlapWarning(
          result.overlapKind === 'PENDING'
            ? `You already have a pending ${o.type} request for these dates (${range}). Submitting again would create a duplicate.`
            : `This leave overlaps with an approved ${o.type} from ${range}.`
        );
      } else {
        setOverlapWarning(null);
      }
    };
    check();
    return () => { cancelled = true; };
  }, [user?.email, form.startDate, form.endDate, duration, editingLeave?.id]);

  const handleOpenModal = (dateStr?: string) => {
    const d = dateStr || today;
    setEditingLeave(null);
    setForm((prev) => ({
      ...prev,
      startDate: d,
      endDate: d,
      reason: '',
    }));
    setAttachmentName('');
    setIsModalOpen(true);
  };

  /**
   * Open the same drawer to amend an existing request.
   *
   * One form for both actions on purpose: an edit screen that drifts from the apply screen
   * ends up validating differently, and the employee would meet two versions of the same
   * rules. Permission is re-checked here because the row was rendered from data that may be
   * minutes old and the change window can close while the page sits open.
   */
  const handleOpenEdit = (leave: LeaveRequest) => {
    const permitted = canDeleteLeave(leave, viewer, today, lockPeriodDays);
    if (!permitted.allowed) { window.alert(permitted.reason); return; }

    setEditingLeave(leave);
    setForm({
      type: leave.type,
      startDate: leave.startDate,
      endDate: leave.endDate,
      session: (leave.session || 'FULL') as Session,
      reason: leave.reason || '',
    });
    setAttachmentName('');
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingLeave(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    // The button is already disabled; this covers Enter in a field submitting the form anyway.
    if (balanceError) { window.alert(balanceError); return; }
    setSubmitting(true);
    try {
      const endDate = isHalfDay ? form.startDate : form.endDate;

      if (editingLeave) {
        // Amending sends the request back for approval — see LeaveService.amendLeave.
        const wasApproved = String(editingLeave.status).toUpperCase() === 'APPROVED';
        const amended = await LeaveService.amendLeave({
          id: editingLeave.id,
          type: form.type,
          startDate: form.startDate,
          endDate,
          session: form.session,
          reason: form.reason.trim(),
          updatedBy: user.email,
        });

        const range = form.startDate === endDate ? form.startDate : `${form.startDate} to ${endDate}`;
        notifyLeaveAmended(
          user.email, user.name || user.email, String(form.type), range, wasApproved, user.managerEmail
        ).catch(err => console.warn('[ApplyLeave] amend notification failed:', err));
        // Only once the amendment was actually recorded, and from the row that came back rather
        // than from the form — the two agree today, and an email describing dates the server
        // rejected would be the one that mattered when they stop agreeing.
        if (amended) {
          await announceLeaveAmended(amended, wasApproved, {
            managerEmail: user.managerEmail,
            managerName: approver.name,
          });
        }
      } else {
        await LeaveService.createLeave({
          employeeEmail: user.email,
          employeeName: user.name,
          department: user.department || 'General',
          type: form.type,
          startDate: form.startDate,
          endDate,
          session: form.session,
          reason: form.reason.trim(),
          managerEmail: user.managerEmail || '',
          // For the email only; the row has no such column. See LeaveService.createLeave.
          managerName: approver.name,
        } as any);
      }

      setIsModalOpen(false);
      setEditingLeave(null);
      setForm((prev) => ({ ...prev, reason: '' }));
      setAttachmentName('');
      await Promise.all([loadBalances(), loadLeaves()]);
    } finally {
      setSubmitting(false);
    }
  };

  // Calendar days grid
  const calendarDays = useMemo(() => getMonthDays(monthDate), [monthDate]);

  // Map date -> leave entries
  /**
   * The shared team calendar: every employee's leave, grouped by date.
   *
   * Deliberately not filtered to the signed-in user — the whole point is that everyone
   * sees who is away on a given day before booking their own leave.
   *
   * Rejected and cancelled requests are excluded: they represent leave that is not
   * happening, and leaving them in made the calendar overstate how many people were out.
   * The cell keeps the compact "Usha (CL)" form; the day popup carries the full name and
   * leave type.
   */
  const entriesByDate = useMemo(() => {
    const map = new Map<string, DayEntry[]>();
    leaves
      // Status-based, and company-wide: every live request — approved, submitted, or awaiting
      // a cancellation decision — is on the calendar for everyone, and only settled ones
      // (rejected, cancelled) are dropped. See canSeeLeaveOnCalendar for why each qualifies.
      .filter((leave) => canSeeLeaveOnCalendar(leave, viewer))
      .forEach((leave) => {
        eachDate(leave.startDate, leave.endDate).forEach((date) => {
          const list = map.get(date) || [];
          list.push({
            cellName: shortNameOf(leave.employeeName, leave.employeeEmail),
            fullName: fullNameOf(leave.employeeName, leave.employeeEmail),
            email: leave.employeeEmail,
            abbreviation: leaveTypeAbbreviation(leave.type),
            typeLabel: String(leave.type),
            status: leave.status,
            session: leave.session,
          });
          map.set(date, list);
        });
      });
    /*
     * Booked absences first, then the ones still being decided.
     *
     * A boolean "is it approved" no longer orders this: a leave awaiting a cancellation
     * decision is booked and the person is still expected to be away, so it belongs with the
     * approved ones rather than below a request nobody has approved yet.
     */
    const stageRank = (status: string): number => {
      if (status === LeaveStatus.APPROVED) return 0;
      if (status === LeaveStatus.CANCELLATION_REQUESTED) return 1;
      return 2;
    };
    map.forEach((list) => list.sort((a, b) => stageRank(a.status) - stageRank(b.status)));
    return map;
  }, [leaves, viewer]);

  const changeMonth = (offset: number) => {
    setMonthDate((current) => new Date(current.getFullYear(), current.getMonth() + offset, 1));
  };

  const handleMonthSelect = (mIndex: number) => {
    setMonthDate(new Date(year, mIndex, 1));
  };

  const handleYearSelect = (yValue: number) => {
    setMonthDate(new Date(yValue, month, 1));
  };

  const monthLabel = monthDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  // PDF Export
  const handleExport = () => {
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text(`Leave Calendar Report - ${monthLabel}`, 14, 18);
    doc.setFontSize(10);
    doc.text(`Generated on ${new Date().toLocaleDateString()}`, 14, 25);

    // Same visibility rule as the calendar: the export must not become a way around it.
    // It previously included every employee's leave *reason*, which any employee could
    // download — reasons are frequently medical or personal.
    const monthLeaves = leaves
      .filter((l) => canSeeLeaveOnCalendar(l, viewer))
      .filter((l) => {
        const s = parseISODate(l.startDate);
        const e = parseISODate(l.endDate);
        return (s.getFullYear() === year && s.getMonth() === month) || (e.getFullYear() === year && e.getMonth() === month);
      });

    autoTable(doc, {
      startY: 32,
      head: [['Employee', 'Department', 'Type', 'Start Date', 'End Date', 'Days', 'Status', 'Reason']],
      body: monthLeaves.map((l) => [
        l.employeeName,
        l.department || 'General',
        l.type,
        l.startDate,
        l.endDate,
        String(leaveDaysFor(l)),
        l.status,
        canSeeLeaveReason(l, viewer) ? (l.reason || '—') : '—',
      ]),
    });

    doc.save(`Leave_Calendar_${MONTH_NAMES[month]}_${year}.pdf`);
  };

  // My Leave History
  const myLeaves = useMemo(() => {
    if (!user?.email) return [];
    return leaves
      .filter((l) => sameIdentity(l.employeeEmail, user.email))
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [leaves, user?.email]);

  /**
   * Honour a #my-leave-history hash by scrolling the history table into view.
   *
   * The browser cannot do this itself: the page is client-rendered and the table only exists
   * once the leave list has loaded, by which time the initial hash jump has long passed.
   * Depending on myLeaves.length is what makes it wait for the rows to arrive.
   */
  const { hash } = useLocation();
  useEffect(() => {
    if (hash !== `#${LEAVE_HISTORY_ANCHOR}`) return;
    const target = document.getElementById(LEAVE_HISTORY_ANCHOR);
    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [hash, myLeaves.length]);

  return (
    <section className="leave-page">
      {/* ── Top Bar Header matching target design ── */}
      <div className="lc-top-bar">
        <h1 className="lc-page-title">Leave Calendar</h1>
        <div className="lc-top-controls">
          <select
            className="lc-select"
            value={month}
            onChange={(e) => handleMonthSelect(Number(e.target.value))}
            aria-label="Select month"
          >
            {MONTH_NAMES.map((name, i) => (
              <option key={name} value={i}>{name}</option>
            ))}
          </select>

          <select
            className="lc-select"
            value={year}
            onChange={(e) => handleYearSelect(Number(e.target.value))}
            aria-label="Select year"
          >
            {/* Stepping back past January walks the calendar into a year the picker
                doesn't offer; list it anyway so the control never renders blank and
                misreports which year is on screen. */}
            {Array.from(new Set([...LEAVE_YEARS, year])).sort((a, b) => a - b).map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>

          <button type="button" className="lc-export-btn" onClick={handleExport}>
            Export
          </button>

          <button type="button" className="lc-apply-btn" onClick={() => handleOpenModal()}>
            + Apply Leave
          </button>
        </div>
      </div>

      {/* ── Main Leave Calendar Card ── */}
      <div className="lc-card">
        <div className="lc-card-header">
          <button
            type="button"
            className="lc-nav-btn"
            onClick={() => changeMonth(-1)}
            aria-label="Previous month"
          >
            ‹
          </button>
          <h2 className="lc-card-title">{monthLabel}</h2>
          <button
            type="button"
            className="lc-nav-btn"
            onClick={() => changeMonth(1)}
            aria-label="Next month"
          >
            ›
          </button>
        </div>

        {/* 7 Columns Weekday Row */}
        <div className="lc-weekdays-row">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
            <div key={day} className="lc-weekday-cell">
              {day}
            </div>
          ))}
        </div>

        {/* 7x5 or 7x6 Calendar Grid */}
        <div className="lc-grid">
          {calendarDays.map((day) => {
            const dayEntries = entriesByDate.get(day.date) || [];
            const holidayName = holidaysByDate.get(day.date);
            const visibleEntries = dayEntries.slice(0, 2);
            const hasMore = dayEntries.length > visibleEntries.length;
            const isSelected = selectedDate === day.date;

            return (
              <div
                key={day.date}
                className={`lc-grid-cell ${day.inMonth ? 'in-month' : 'out-month'} ${isSelected ? 'selected' : ''}`}
                onClick={() => {
                  setSelectedDate(day.date);
                  // A day with people on leave opens the roll call first — clicking straight
                  // into the apply form used to hide exactly the information you need before
                  // booking. An empty day still goes straight to applying.
                  if (dayEntries.length > 0) setDayDetail(day.date);
                  else handleOpenModal(day.date);
                }}
              >
                <div className="lc-day-num">{parseISODate(day.date).getDate()}</div>
                {holidayName && <div className="lc-holiday-tag" title={holidayName}>{holidayName}</div>}

                {visibleEntries.length > 0 && (
                  <div className="lc-entries-list">
                    {visibleEntries.map((entry, idx) => (
                      /*
                       * Name, type, then the stage.
                       *
                       * The stage was previously encoded only as a CSS class — is-approved vs
                       * is-pending — so a booked absence and one somebody is trying to cancel
                       * looked different but neither said which it was, and colour was the only
                       * cue. The word is now on the cell; the full stage is in the title for the
                       * one value short enough to be abbreviated here.
                       */
                      <div
                        key={idx}
                        className={`lc-entry-item ${entry.status === LeaveStatus.APPROVED ? 'is-approved' : 'is-pending'}`}
                        title={`${entry.fullName} — ${entry.typeLabel} — ${leaveStageLabel(entry.status)}`}
                      >
                        <span className="lc-entry-name">{entry.cellName}</span>
                        <span className="lc-entry-type">({entry.abbreviation})</span>
                        <span className="lc-entry-stage">{leaveStageLabel(entry.status)}</span>
                      </div>
                    ))}
                    {hasMore && (
                      <button
                        type="button"
                        className="lc-more-tag"
                        onClick={(e) => { e.stopPropagation(); setDayDetail(day.date); }}
                      >
                        +{dayEntries.length - visibleEntries.length} more
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Who is away on this day ──
          Every employee's leave for the clicked date, with full names and full leave
          types. This is what "+N more" now leads to; previously it was a dead label and
          anything past the second entry was unreachable. */}
      {dayDetail && (
        <div className="lc-daymodal-backdrop" onClick={() => setDayDetail(null)}>
          <div
            className="lc-daymodal"
            role="dialog"
            aria-modal="true"
            aria-label={`Leave on ${dayDetail}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="lc-daymodal-head">
              <h3>
                {parseISODate(dayDetail).toLocaleDateString('en-US', {
                  weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
                })}
              </h3>
              <button type="button" className="lc-daymodal-close" onClick={() => setDayDetail(null)} aria-label="Close">×</button>
            </div>

            {holidaysByDate.get(dayDetail) && (
              <p className="lc-daymodal-holiday">🎉 {holidaysByDate.get(dayDetail)}</p>
            )}

            <p className="lc-daymodal-count">
              {(entriesByDate.get(dayDetail) || []).length} person(s) on leave
            </p>

            <ul className="lc-daymodal-list">
              {(entriesByDate.get(dayDetail) || []).map((entry, idx) => (
                <li key={`${entry.email}-${idx}`} className="lc-daymodal-item">
                  <span className="lc-daymodal-who">
                    <strong>{entry.fullName}</strong>
                    <span className="lc-daymodal-type">
                      {entry.typeLabel}
                      {entry.session && entry.session !== 'FULL' &&
                        ` — ${entry.session === 'HALF_AM' ? 'Half day (AM)' : 'Half day (PM)'}`}
                    </span>
                  </span>
                  <span className={`lb-status-badge status-${String(entry.status).toLowerCase()}`}>
                    {leaveStageLabel(entry.status)}
                  </span>
                </li>
              ))}
            </ul>

            <div className="lc-daymodal-actions">
              <button
                type="button"
                className="lb-btn-submit"
                onClick={() => { const d = dayDetail; setDayDetail(null); handleOpenModal(d); }}
              >
                Apply leave for this date
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── My Leave History Table ──
           The id is a scroll target: this page opens on the calendar, so anything linking
           here to show someone their own requests (the dashboard's "View All") would
           otherwise land them at the top of a month grid with the list below the fold. */}
      <div className="lb-summary-header" id={LEAVE_HISTORY_ANCHOR} style={{ marginTop: 32 }}>
        <div className="lb-summary-title">
          <h2>My Leave History</h2>
        </div>
        {myLeaves.length === 0 ? (
          <p className="lb-ineligible-desc">No leave requests found.</p>
        ) : (
          <table className="lb-history-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>From</th>
                <th>To</th>
                <th>Session</th>
                <th>Days</th>
                <th>Status</th>
                <th>Reason</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {myLeaves.map((l) => {
                const deletion = canDeleteLeave(l, viewer, today, lockPeriodDays);
                return (
                  <tr key={l.id}>
                    <td>{l.type}</td>
                    <td>{l.startDate}</td>
                    <td>{l.endDate}</td>
                    <td>{l.session && l.session !== 'FULL' ? (l.session === 'HALF_AM' ? 'Half (AM)' : 'Half (PM)') : 'Full'}</td>
                    <td className="lb-days-cell">{leaveDaysFor(l)}</td>
                    {/* The badge says what state the request is in; the line under it says who
                        it is waiting on. "Submitted" alone told the employee their request had
                        left their hands and nothing about who now holds it. */}
                    <td>
                      <span className={`lb-status-badge status-${String(l.status).toLowerCase()}`}>{l.status}</span>
                      {pendingApprovalLabel(l.status, approver) && (
                        <span className="lb-pending-with">{pendingApprovalLabel(l.status, approver)}</span>
                      )}
                    </td>
                    <td>{l.reason}</td>
                    <td>
                      {/* Edit only. Withdraw is gone: deleting a request outright was the
                          stand-in for cancellation from before Leave Cancellation existed, and
                          keeping both left two different ways to undo a request — one that
                          erases it with no approval and no record, and one that asks the
                          manager and leaves an audit trail. Cancellation is the structured
                          path, so it is now the only one. */}
                      {deletion.allowed ? (
                        <span className="lb-row-actions">
                          <button
                            type="button"
                            className="lb-btn-edit-leave"
                            onClick={() => handleOpenEdit(l)}
                          >
                            Edit
                          </button>
                        </span>
                      ) : (
                        /* The reason is on the disabled state rather than hidden, so the
                           employee learns why the window closed instead of wondering where
                           the button went. */
                        <span className="lb-withdraw-blocked" title={deletion.reason}>
                          Locked
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Apply Leave Right-Side Slide-Over Drawer (Matching New Expense) ── */}
      {isModalOpen && (
        <div className="lc-drawer-backdrop" onClick={handleCloseModal}>
          <div className="lc-drawer-panel" onClick={(e) => e.stopPropagation()}>
            <div className="lc-drawer-header">
              <h2 className="lc-drawer-title">{editingLeave ? 'Edit Leave Request' : 'Apply for Leave'}</h2>
              <button
                type="button"
                className="lc-drawer-close-pill"
                onClick={handleCloseModal}
                aria-label="Close drawer"
              >
                ✕ Close
              </button>
            </div>

            <form onSubmit={handleSubmit} className="lc-drawer-body">
              <div className="leave-form-row">
                <div className="leave-form-group">
                  <label className="leave-form-label">Employee</label>
                  <input type="text" className="leave-form-input readonly" value={user?.name || ''} readOnly />
                </div>
                <div className="leave-form-group">
                  <label className="leave-form-label">Department</label>
                  <input type="text" className="leave-form-input readonly" value={user?.department || 'General'} readOnly />
                </div>
              </div>

              <div className="leave-form-group">
                <label className="leave-form-label">
                  Leave Type <span className="required">*</span>
                </label>
                <select
                  className="leave-form-select"
                  value={form.type}
                  onChange={(e) => setForm((prev) => ({ ...prev, type: e.target.value as LeaveType }))}
                >
                  {leaveTypeOptions.map((type) => {
                    // Disabled, not hidden: an employee who sees Casual Leave missing assumes a
                    // bug; one who sees "no balance left" knows why.
                    const none = availableFor(type) === 0;
                    return (
                      <option key={type} value={type} disabled={none}>
                        {none ? `${type} — no balance left` : type}
                      </option>
                    );
                  })}
                </select>
              </div>

              {selectedBalance ? (
                <div className="leave-balance-info-card">
                  <span aria-hidden="true">ℹ️</span>
                  <span>
                    Current balance for {form.type}: <strong>{selectedBalance.dojMissing ? 'DOJ not set' : `${selectedBalance.balance} day(s)`}</strong>
                    {!selectedBalance.dojMissing && selectedBalance.pending > 0 && <> · {selectedBalance.pending} pending</>}
                    {/* The deadline belongs here more than anywhere else in the app: this is
                        the screen where the days get spent, so "2 expiring 15 Sep 2026" is
                        the difference between using them and watching them lapse. Comp-Off
                        especially — its window runs from the date worked, so days expire
                        mid-year rather than at the year end an employee might assume. */}
                    {!selectedBalance.dojMissing && expirySummary(selectedBalance.expiringGrants) && (
                      <> · <strong title={expiryTooltip(selectedBalance.expiringGrants)}>
                        {expirySummary(selectedBalance.expiringGrants)}
                      </strong></>
                    )}
                  </span>
                </div>
              ) : (
                <div className="leave-balance-info-card muted">
                  <span aria-hidden="true">ℹ️</span>
                  <span>No tracked balance for {form.type} (unpaid / no accrual).</span>
                </div>
              )}

              <div className="leave-form-row">
                <div className="leave-form-group">
                  <label className="leave-form-label">
                    From Date <span className="required">*</span>
                  </label>
                  <input
                    type="date"
                    className="leave-form-input"
                    value={form.startDate}
                    /* No `min`: applying for a past date is allowed. The lock period governs
                       editing and deletion, not when leave may be requested. */
                    onChange={(e) => setForm((prev) => ({
                      ...prev,
                      startDate: e.target.value,
                      endDate: prev.endDate < e.target.value ? e.target.value : prev.endDate,
                    }))}
                    required
                  />
                </div>
                <div className="leave-form-group">
                  <label className="leave-form-label">
                    To Date <span className="required">*</span>
                  </label>
                  <input
                    type="date"
                    className="leave-form-input"
                    value={isHalfDay ? form.startDate : form.endDate}
                    min={form.startDate}
                    disabled={isHalfDay}
                    onChange={(e) => setForm((prev) => ({ ...prev, endDate: e.target.value }))}
                    required
                  />
                </div>
              </div>

              <div className="leave-form-group">
                <label className="leave-form-label">Session</label>
                <select
                  className="leave-form-select"
                  value={form.session}
                  onChange={(e) => setForm((prev) => ({ ...prev, session: e.target.value as Session }))}
                >
                  <option value="FULL">Full Day</option>
                  <option value="HALF_AM">Half Day — First Half (AM)</option>
                  <option value="HALF_PM">Half Day — Second Half (PM)</option>
                </select>
              </div>

              <div className="leave-duration-card">
                <span>Calculated Duration: <strong>{formatLeaveDays(duration)}</strong></span>
                {/* Naming the skipped days stops the total looking like a miscount. */}
                {excludedDays.length > 0 && (
                  <span className="leave-duration-excluded">
                    {excludedDays.length} non-working day(s) in this range are not deducted
                    {excludedDays.length <= 3 && ` (${excludedDays.join(', ')})`}
                  </span>
                )}
                {duration === 0 && (
                  <span className="leave-duration-zero">
                    This range contains no working days — nothing would be deducted.
                  </span>
                )}
              </div>

              {balanceError && (
                <div className="leave-form-error" role="alert" style={{ color: '#b91c1c', fontSize: 13, margin: '4px 0 8px' }}>
                  {balanceError}
                </div>
              )}

              {overlapWarning && (
                <div className="leave-overlap-warning-card">
                  <span aria-hidden="true">⚠️</span>
                  <span>{overlapWarning}</span>
                </div>
              )}

              <div className="leave-form-group">
                <label className="leave-form-label">
                  Reason for Leave <span className="required">*</span>
                </label>
                <textarea
                  className="leave-form-textarea"
                  value={form.reason}
                  onChange={(e) => setForm((prev) => ({ ...prev, reason: e.target.value }))}
                  rows={3}
                  placeholder="Optional details about this leave request..."
                  required
                />
              </div>

              <div className="leave-form-group">
                <label className="leave-form-label">Attachment (optional)</label>
                <div className="leave-file-dropzone">
                  <input
                    type="file"
                    id="leave-file-input"
                    className="leave-file-input-hidden"
                    onChange={(e) => setAttachmentName(e.target.files?.[0]?.name || '')}
                  />
                  <label htmlFor="leave-file-input" className="leave-file-label">
                    <span aria-hidden="true">📎</span>
                    <span>{attachmentName ? `Selected: ${attachmentName}` : 'Add Attachment'}</span>
                  </label>
                </div>
              </div>

              <div className="lc-drawer-actions">
                <button
                  type="button"
                  className="leave-btn-outline"
                  onClick={handleCloseModal}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="leave-btn-primary"
                  /* Blocked on a lock-period violation and on an overlap: submitting a
                     duplicate of a request already in the queue is never intended. */
                  disabled={submitting || !form.reason.trim() || duration <= 0 || !!overlapWarning || !!balanceError}
                >
                  {submitting ? 'Saving…' : editingLeave ? 'Save Changes' : 'Submit Leave'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
};

export default ApplyLeavePage;
